// @catalog F.010
/**
 * ForecastPage — orchestrator del rediseño /compras/forecast (handoff Fase 8).
 *
 * Compone:
 *   - ForecastToolbar (header sticky 1 fila — feature 3)
 *   - ForecastTable (11 columnas sticky — feature 5) + ForecastProductRow (feature 6)
 *   - SideFilters (panel inline derecho 6 secciones — feature 8)
 *   - SelectionBar (sticky bottom — feature 7)
 *   - InventoryPopover + SalesPopover (feature 9 + 10) — primary;
 *     InventoryDetailsDialog / SalesDetailsDialog modales como fallback.
 *   - ProductDetailDrawer (rediseño previo, vía ForecastPageDialogs).
 *
 * Conserva:
 *   - Hooks de fetch/scroll/selección legacy (useFetchProducts, useSearchAndSortIndex,
 *     useSelectionResync, useDialogHandlers, useCalcHandlers, useHScrollSync,
 *     useBodyOverflowLock) intactos — solo cambia la presentación.
 *   - State del calc-dialog (selectedIds, dialogBaseSelectedIds, horizonAddedIds,
 *     calcDialogOpen, confirmDialogOpen).
 *
 * data-accent="teal" en wrapper raíz activa tokens compras (feature 1).
 *
 * Constraint: P7 ≤500 LoC.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import {
  type CalcResult,
  type SamplesByProduct,
} from "../components/Forecast/SelectedProductsDialog";
import { filterAndSortProducts } from "./forecast/filter-and-sort";
import {
  useHScrollSync,
  useHScrollMeasureOnChange,
} from "./forecast/use-h-scroll-sync";
import { ForecastPageDialogs } from "./forecast/ForecastPageDialogs";
import { buildSelectedProductsForDialog } from "./forecast/build-selected-for-dialog";
import { toggleActiveForPurchase } from "./forecast/toggle-active-for-purchase";
import { useSelectionHandlers } from "./forecast/use-selection-handlers";
import { usePolicyDefaults } from "./forecast/use-policy-defaults";
import { useDialogHandlers } from "./forecast/use-dialog-handlers";
import { useCalcHandlers } from "./forecast/use-calc-handlers";
import { ForecastToolbar } from "./forecast/ForecastToolbar";
import { ForecastTable, type ForecastTableRowProps } from "./forecast/ForecastTable";
import { ForecastProductRow } from "../components/Forecast/ForecastProductRow";
import { SelectionBar } from "./forecast/SelectionBar";
import { SideFilters, type AdvancedFilterGroup } from "./forecast/SideFilters";
import { InventoryPopover } from "./forecast/InventoryPopover";
import { SalesPopover } from "./forecast/SalesPopover";
import {
  countActiveAdvancedFilters,
  emptyAdvancedFilters,
  type AdvancedFilters,
} from "./forecast/selectors";
import { useSelectionResync } from "./forecast/use-selection-resync";
import {
  ForecastPageLoading,
  ForecastPageError,
} from "./forecast/ForecastPageStates";
import {
  useFetchProducts,
  useBodyOverflowLock,
  useSearchAndSortIndex,
  usePageResetOnFilterChange,
} from "./forecast/use-forecast-effects";
import { exportForecastTable } from "./forecast/export-table-csv";
import { getDisplayText } from "./forecast/text-utils";
import { usePageTitle } from "../context/PageTitleContext";
import type { ProductData, SortField, SortOrder } from "./forecast/types";
import { useForecastPopovers } from "./forecast/use-forecast-popovers";

export function ForecastPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [products, setProducts] = useState<ProductData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sideFiltersOpen, setSideFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(emptyAdvancedFilters);
  const [hideDeselected, setHideDeselected] = useState(false);
  // Item 1: vista default solo-activos; toggle muestra solo apagados (OFF).
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const { popover, clearPopover, openInventoryPopover, openSalesPopover } =
    useForecastPopovers();
  const [sortField, setSortField] = useState<SortField>("recentSalesAmount");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(200);
  // Defaults de policies (horizonte B4.1 + crecimiento 2026-06-10).
  const { forecastMonths, setForecastMonths, defaultGrowthPct } =
    usePolicyDefaults();
  const [parentCalcResults, setParentCalcResults] = useState<CalcResult[]>([]);
  const [parentCalcLoading, setParentCalcLoading] = useState(false);
  const [parentSamplesByProduct, setParentSamplesByProduct] = useState<SamplesByProduct>({});
  const [parentGlobalMarginRatio, setParentGlobalMarginRatio] = useState<number | null>(null);
  const searchIndexRef = useRef<Record<string, string>>({});
  const sortNameRef = useRef<Record<string, string>>({});
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialogBaseSelectedIds, setDialogBaseSelectedIds] = useState<Set<string>>(new Set());
  const [dialogBaseEstimatedAmount, setDialogBaseEstimatedAmount] = useState<number | null>(null);
  const [horizonAddedIds, setHorizonAddedIds] = useState<Set<string>>(new Set());
  const [dialogSelectionSyncLoading, setDialogSelectionSyncLoading] = useState(false);
  const restoreSelectionOnDialogCloseRef = useRef(true);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | null>(null);
  const [, setSelectedSupplierId] = useState<number | null>(null);
  const [selectedSupplierEstimatedAmount, setSelectedSupplierEstimatedAmount] = useState<number | null>(null);
  const [, setSupplierSelectLoading] = useState(false);
  const [, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const scrollHostRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const xScrollRef = useRef<HTMLDivElement | null>(null);
  const hContentRef = useRef<HTMLDivElement | null>(null);
  const [, setHScrollWidth] = useState<number>(0);
  const [, setBottomBarLeft] = useState<number>(0);
  const [, setBottomBarWidth] = useState<number>(0);

  useFetchProducts({ setProducts, setLoading, setError });
  useHScrollSync({ scrollHostRef, xScrollRef, bottomScrollRef, hContentRef, setHScrollWidth, setBottomBarLeft, setBottomBarWidth });
  useHScrollMeasureOnChange({
    scrollHostRef, xScrollRef, bottomScrollRef, hContentRef,
    setHScrollWidth, setBottomBarLeft, setBottomBarWidth,
    deps: [products.length, page, rowsPerPage],
  });

  useEffect(() => {
    setTitle("Forecast de Compras");
    return () => setTitle("");
  }, [setTitle]);

  useBodyOverflowLock();
  useSearchAndSortIndex({ products, searchIndexRef, sortNameRef, getDisplayText });

  const filteredAndSortedProducts = useMemo(
    () =>
      filterAndSortProducts({
        products,
        debouncedSearchTerm: search,
        debouncedSecondSearchTerm: "",
        searchIndex: searchIndexRef.current,
        sortNameMap: sortNameRef.current,
        inactiveViewMode: showInactiveOnly ? "onlyInactive" : "hidden",
        dciFilterActive: false,
        categoryFilter: "",
        hideDeselected,
        selectedIds,
        sortField,
        sortOrder,
        getDisplayText,
        advancedFilters,
      }),
    [
      products,
      search,
      sortField,
      sortOrder,
      selectedIds,
      advancedFilters,
      hideDeselected,
      showInactiveOnly,
    ],
  );

  useSelectionResync({
    filteredAndSortedProducts,
    setSelectedIds,
  });

  usePageResetOnFilterChange({
    setPage,
    deps: [
      search,
      sortField,
      sortOrder,
      products.length,
      advancedFilters,
      hideDeselected,
      showInactiveOnly,
    ],
  });

  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedProducts.slice(start, start + rowsPerPage);
  }, [filteredAndSortedProducts, page, rowsPerPage]);

  const activeAdvancedCount = useMemo(
    () => countActiveAdvancedFilters(advancedFilters),
    [advancedFilters],
  );
  const hasActiveFilters =
    !!search || activeAdvancedCount > 0 || hideDeselected;

  // Callbacks de selección (P7 split 2026-06-10 — ver use-selection-handlers).
  const {
    isSelected,
    toggleRow,
    toggleHeaderSelection,
    selectAllFiltered,
    deselectAllFiltered,
    toggleHideDeselected,
    runSmartPreselect,
    handleRemoveSelectedProduct,
  } = useSelectionHandlers({
    products,
    filteredAndSortedProducts,
    selectedIds,
    setSelectedIds,
    setDialogBaseSelectedIds,
    setSelectedSupplierEstimatedAmount,
    setHideDeselected,
  });

  const handleSort = useCallback((field: SortField) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prevField;
      }
      setSortOrder("asc");
      return field;
    });
  }, []);

  const handleToggleActiveForPurchase = useCallback(
    (unifiedProductId: string) =>
      toggleActiveForPurchase({ unifiedProductId, setProducts, setSelectedIds }),
    [],
  );

  const {
    handleOpenInventory,
    handleOpenSales,
    handleOpenDetail,
    handleCloseActionsMenu,
    handleCalcDialogClose,
    handleConfirmDialogClose,
  } = useDialogHandlers({
    setSelectedProductId, setSelectedProductName,
    setInventoryDialogOpen, setSalesDialogOpen, setDetailDialogOpen,
    setSelectedSupplierId, setSupplierSelectLoading,
    setActionsAnchorEl, setSelectedIds, setSelectedSupplierEstimatedAmount,
    setHorizonAddedIds, setDialogSelectionSyncLoading,
    setCalcDialogOpen, setConfirmDialogOpen, setConfirmAction,
    restoreSelectionOnDialogCloseRef, dialogBaseSelectedIds, dialogBaseEstimatedAmount,
  });

  const {
    runForecastCalc, openCalcDialog,
    handleDialogMonthsChange, handleConfirmAction,
    handleConfirmDialogConfirm,
  } = useCalcHandlers({
    selectedIds, products, filteredAndSortedProducts,
    parentGlobalMarginRatio, forecastMonths, selectedSupplierEstimatedAmount,
    confirmAction, setProducts, setSelectedIds,
    setParentCalcResults, setParentSamplesByProduct, setParentCalcLoading,
    setParentGlobalMarginRatio, setForecastMonths,
    setDialogBaseSelectedIds, setDialogBaseEstimatedAmount, setHorizonAddedIds,
    setCalcDialogOpen, setConfirmAction, setConfirmDialogOpen,
    restoreSelectionOnDialogCloseRef,
    closeActionsMenu: handleCloseActionsMenu, closeConfirmDialog: handleConfirmDialogClose,
  });

  const toggleAdvancedFilter = useCallback(
    (group: AdvancedFilterGroup, value: string) => {
      setAdvancedFilters((prev) => {
        const nextSet = new Set(prev[group]);
        if (nextSet.has(value)) nextSet.delete(value);
        else nextSet.add(value);
        return { ...prev, [group]: nextSet };
      });
    },
    [],
  );

  // Adapter: ForecastTable RowComponent → ForecastProductRow (con popovers cableados)
  const RowAdapter = useCallback(
    (rp: ForecastTableRowProps): React.ReactElement => (
      <ForecastProductRow
        product={rp.product}
        selected={rp.selected}
        displayName={rp.displayName}
        onToggleRow={rp.onToggleRow}
        onOpenInventory={(id, _name, e) => {
          if (e) openInventoryPopover(e, id);
          else handleOpenInventory(id, rp.displayName);
        }}
        onOpenSales={(id, _name, e) => {
          if (e) openSalesPopover(e, id);
          else handleOpenSales(id, rp.displayName);
        }}
        onOpenDetail={rp.onOpenDetail}
        onToggleActiveForPurchase={rp.onToggleActiveForPurchase}
      />
    ),
    [handleOpenInventory, handleOpenSales, openInventoryPopover, openSalesPopover],
  );

  const selectedProductsForDialog = useMemo(
    () => buildSelectedProductsForDialog(products, selectedIds, forecastMonths, horizonAddedIds),
    [horizonAddedIds, products, selectedIds, forecastMonths],
  );

  const popoverProduct = useMemo(() => {
    if (!popover) return null;
    return products.find((p) => p.unifiedProductId === popover.productId) ?? null;
  }, [popover, products]);

  if (loading) return <ForecastPageLoading />;
  if (error) return <ForecastPageError message={error} />;

  return (
    <Box
      data-accent="teal"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollHostRef}
        data-testid="forecast-scroll-host"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ForecastToolbar
          totalCount={products.length}
          filteredCount={filteredAndSortedProducts.length}
          hasActiveFilters={hasActiveFilters}
          search={search}
          onSearch={setSearch}
          sideFiltersOpen={sideFiltersOpen}
          onToggleSideFilters={() => setSideFiltersOpen((v) => !v)}
          activeAdvancedCount={activeAdvancedCount}
          onExport={() => exportForecastTable(filteredAndSortedProducts, "filtrados")}
          horizonMonths={forecastMonths}
          onHorizonChange={setForecastMonths}
          onPreselect={runSmartPreselect}
          onSelectAllFiltered={selectAllFiltered}
          onDeselectAllFiltered={deselectAllFiltered}
          hideDeselected={hideDeselected}
          onToggleHideDeselected={toggleHideDeselected}
          showInactiveOnly={showInactiveOnly}
          onToggleShowInactive={() => setShowInactiveOnly((v) => !v)}
        />

        <SelectionBar
          selectedIds={selectedIds}
          products={products}
          onCalcForecast={openCalcDialog}
          onActivate={() => handleConfirmAction("activate")}
          onDeactivate={() => handleConfirmAction("deactivate")}
          onExport={() =>
            exportForecastTable(
              products.filter((p) => selectedIds.has(p.unifiedProductId)),
              "seleccionados",
            )
          }
          onClear={() => setSelectedIds(new Set())}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <ForecastTable
              xScrollRef={xScrollRef}
              hContentRef={hContentRef}
              paginatedProducts={paginatedProducts}
              filteredAndSortedProductsLength={filteredAndSortedProducts.length}
              selectedIds={selectedIds}
              isSelected={isSelected}
              toggleRow={toggleRow}
              toggleHeaderSelection={toggleHeaderSelection}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              onOpenInventoryPopover={(e, id) => openInventoryPopover(e, id)}
              onOpenSalesPopover={(e, id) => openSalesPopover(e, id)}
              onOpenDetail={handleOpenDetail}
              onToggleActiveForPurchase={handleToggleActiveForPurchase}
              getDisplayText={getDisplayText}
              RowComponent={RowAdapter}
            />
          </Box>
          <SideFilters
            open={sideFiltersOpen}
            onClose={() => setSideFiltersOpen(false)}
            advancedFilters={advancedFilters}
            onToggleFilter={toggleAdvancedFilter}
            onToggleActiveOnly={(v) =>
              setAdvancedFilters((prev) => ({ ...prev, activeOnly: v }))
            }
            onClear={() => setAdvancedFilters(emptyAdvancedFilters())}
            products={products}
          />
        </Box>

        <Stack ref={bottomScrollRef} sx={{ height: 0 }} />
      </Box>

      <InventoryPopover
        open={popover?.type === "inventory"}
        anchorRect={popover?.anchorRect ?? null}
        product={popover?.type === "inventory" ? popoverProduct : null}
        onClose={clearPopover}
        onOpenFullDialog={() => {
          if (popover && popoverProduct) {
            handleOpenInventory(popover.productId, getDisplayText(popoverProduct.unifiedName));
          }
          clearPopover();
        }}
      />
      <SalesPopover
        open={popover?.type === "sales"}
        anchorRect={popover?.anchorRect ?? null}
        product={popover?.type === "sales" ? popoverProduct : null}
        onClose={clearPopover}
        onOpenFullDialog={() => {
          if (popover && popoverProduct) {
            handleOpenSales(popover.productId, getDisplayText(popoverProduct.unifiedName));
          }
          clearPopover();
        }}
      />

      <ForecastPageDialogs
        inventoryDialogOpen={inventoryDialogOpen}
        salesDialogOpen={salesDialogOpen}
        detailDialogOpen={detailDialogOpen}
        calcDialogOpen={calcDialogOpen}
        confirmDialogOpen={confirmDialogOpen}
        selectedProductId={selectedProductId}
        selectedProductName={selectedProductName}
        setInventoryDialogOpen={setInventoryDialogOpen}
        setSalesDialogOpen={setSalesDialogOpen}
        setDetailDialogOpen={setDetailDialogOpen}
        handleCalcDialogClose={handleCalcDialogClose}
        onCalcDialogClearAfterSuccess={() => {
          restoreSelectionOnDialogCloseRef.current = false;
          setSelectedIds(new Set());
          setDialogBaseSelectedIds(new Set());
          setSelectedSupplierEstimatedAmount(null);
          setDialogBaseEstimatedAmount(null);
          setHorizonAddedIds(new Set());
          setSearch("");
        }}
        selectedProductsForDialog={selectedProductsForDialog}
        selectedSupplierEstimatedAmount={selectedSupplierEstimatedAmount}
        dialogSelectionSyncLoading={dialogSelectionSyncLoading}
        handleDialogMonthsChange={handleDialogMonthsChange}
        forecastMonths={forecastMonths}
        initialGrowthPct={defaultGrowthPct}
        parentCalcResults={parentCalcResults}
        parentCalcLoading={parentCalcLoading}
        parentSamplesByProduct={parentSamplesByProduct}
        parentGlobalMarginRatio={parentGlobalMarginRatio}
        onRecalculate={() => runForecastCalc(forecastMonths)}
        confirmAction={confirmAction}
        selectedIdsSize={selectedIds.size}
        handleConfirmDialogClose={handleConfirmDialogClose}
        handleConfirmDialogConfirm={handleConfirmDialogConfirm}
        onRemoveSelectedProduct={handleRemoveSelectedProduct}
        onToggleActiveForPurchase={handleToggleActiveForPurchase}
      />
    </Box>
  );
}
